/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useDispatch } from "react-redux";
import { Redirect } from "react-router";

import * as UserProfilesActions from "Dataflows/RD/UserProfile/RDUserProfileAction";

import { useAuth } from "Authentication/AuthProvider";
import { AddComment } from "Components/AddComment/AddComment";
import { OperationStatus } from "Constants/OperationStatus";
import { PermissionsEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";

import { CommentsHistory } from "../CommentsHistory/CommentsHistory";
import { IRegionalDirectorCommentsProps } from "../RegionalDirectorComments/IRegionalDirectorCommentsProps";
import { CommentContainer } from "./RegionalDirectorComments.styles";

/**
 * The regional director comments tab.
 * @param {IRegionalDirectorCommentsProps} props The props.
 */
export const RegionalDirectorComments = (props: IRegionalDirectorCommentsProps): ReactElement => {
  const { isUserLoggedIn, loading, hasAnyRoles, account, hasPermission } = useAuth();
  const dispatch = useDispatch();
  const {
    userProfileId,
    userProfileComments,
    fetchUserProfileComments,
    errorMessage,
    isLoading,
    onAddCommentClicked,
    addCommentStatus,
  } = props;

  useEffect(() => {
    if (
      addCommentStatus === OperationStatus.Succeeded ||
      addCommentStatus === OperationStatus.Idle
    ) {
      fetchUserProfileComments(userProfileId);
    }
  }, [addCommentStatus]);

  useEffect(() => {
    return () => {
      dispatch(UserProfilesActions.resetAddUserProfileCommentValues());
    };
  }, []);

  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  const canAddComments = hasPermission(TenantTypesEnum.RD, PermissionsEnum.RD_UPDATE_USERS);

  return (
    <CommentContainer>
      {canAddComments && (
        <AddComment onAddCommentClicked={onAddCommentClicked} addCommentStatus={addCommentStatus} />
      )}
      <CommentsHistory
        userProfileComments={userProfileComments}
        errorMessage={errorMessage}
        isLoading={isLoading}
      />
    </CommentContainer>
  );
};
